import * as i0 from '@angular/core';
import { Directive, Optional, Self, Input } from '@angular/core';
import * as i1 from '@angular/forms';
import { Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { startWith, tap, filter, map, takeUntil } from 'rxjs/operators';
function maskedNumericValueFor(value = '', thousandSeparator = ' ', decimalSeparator = '.', prefix = '', digitsAfterSeparator = 2, maxDigits = 12, allowNegatives = true) {
  const isNegative = allowNegatives && value.toString().match(/-/g)?.length === 1;
  let baseValue = unmaskedNumericValueFor(value, true);
  baseValue = baseValue.length >= 1 && String(parseInt(baseValue, 10)) || '000';
  const integerLength = baseValue.length - digitsAfterSeparator;
  const cents = baseValue.substr(integerLength > 0 && integerLength || 0).padStart(digitsAfterSeparator, '0');
  let integerValue = baseValue.substring(0, baseValue.length - digitsAfterSeparator > maxDigits ? maxDigits : baseValue.length - digitsAfterSeparator).padStart(1, '0');
  if (thousandSeparator) integerValue = integerValue.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator);
  return `${prefix ? `${prefix} ` : ''}${isNegative ? '-' : ''}${integerValue}${decimalSeparator}${cents}`;
}
function unmaskedNumericValueFor(value, removeNegative = false) {
  return removeNegative ? value.toString().replace(/[^0-9]+/g, '') : value.toString().replace(/[^0-9-]+/g, '');
}
function unmaskedValueFor(value) {
  return value.toString().replace(/[^\w]+/g, '');
}
function hasNonDecimalCharacters(value) {
  return !/^-?\d+$/.test(value.toString());
}
function matchAndReplaceFor(text, pattern) {
  let patternOffset = 0;
  const testPositionFunc = (prevText, cur, i) => {
    if (pattern) {
      switch (pattern[i + patternOffset]) {
        case 'D':
          if (/[\d]/.test(cur)) return `${prevText}${cur}`;
          break;
        case 'C':
          if (/[\A-Z, a-z]/.test(cur)) return `${prevText}${cur}`;
          break;
        case 'W':
          if (/[\w]/.test(cur)) return `${prevText}${cur}`;
          break;
        default:
          {
            if (/[^\w]/.test(pattern[i + patternOffset])) {
              patternOffset++;
              return testPositionFunc(`${prevText}${pattern[i + patternOffset - 1]}`, cur, i);
            }
          }
      }
    }
    patternOffset--;
    return `${prevText}`;
  };
  return text.split('').reduce(testPositionFunc, '');
}
function cursorPositionFor(el) {
  return (el.nativeElement || el).selectionStart;
}
/**
 * Set cursor position at a given `nativeEl` from a `ElementRef` or its wrapper.
 */
function setCursorPositionFor(el, nextPos) {
  const nativeEl = el.nativeElement || el;
  nativeEl.selectionStart = nativeEl.selectionEnd = nextPos;
}
/** Adjusts cursorPosition for input element. Skips non decimal/letter chars.
* @param addingAtLeft cursor will it keep its position.
* @param decimalsOnly if should takes into consideration both decimals and letters for determining cursor position */
function nextCursorPositionFor(el, previousValue, nextValue, addingAtLeft = false, decimalsOnly = false, removingAtLeft = false) {
  const initialCursorPosition = cursorPositionFor(el);
  const maskCheck = decimalsOnly ? /[^\d]/ : /[^\w]/;
  const isAdding = removingAtLeft || !addingAtLeft ? nextValue.length > previousValue?.length : nextValue.length >= previousValue?.length;
  let nextCursorPosition = initialCursorPosition;
  if (addingAtLeft && previousValue) {
    if (isAdding) {
      nextCursorPosition += nextValue.length - previousValue.length - 1;
    } else if (previousValue.length > nextValue.length) {
      nextCursorPosition += nextValue.length - previousValue.length + 1;
    } else {
      nextCursorPosition += unmaskedNumericValueFor(previousValue) > unmaskedNumericValueFor(nextValue) ? 1 : 0;
    }
  }
  let testPosition = nextCursorPosition - 1;
  while (maskCheck.test(nextValue[testPosition])) {
    if (isAdding) {
      testPosition++;
      nextCursorPosition++;
    } else {
      testPosition--;
      nextCursorPosition--;
    }
    if (testPosition < 0) {
      nextCursorPosition = initialCursorPosition + 1;
      break;
    }
  }
  return nextCursorPosition;
}
let AngularFormsCurrencyMaskDirective = /*#__PURE__*/(() => {
  class AngularFormsCurrencyMaskDirective {
    constructor(selfNgControl, elRef) {
      this.selfNgControl = selfNgControl;
      this.elRef = elRef;
      this.prefix = '$';
      this.thousandsSeparator = ' ';
      this.decimalSeparator = '.';
      this.digitsAfterSeparator = 2;
      this.maxIntegerDigits = 8;
      this.allowNegatives = false;
      /** For when the form already has a initial value or is expected to boot as `zero *masked*`. */
      this.validateOnInit = true;
      this.valueHasChanged = false;
      this.directiveExists$ = new Subject();
    }
    ngAfterViewInit() {
      this.control = this.selfNgControl?.control ?? this.ngControl;
      if (!this.control) {
        console.warn('AngularFormsCurrencyMaskDirective: A FormControl value is required for the directive to be initiated.');
        return;
      }
      this.nativeEl = this.elRef.nativeElement.hasChildNodes() ? this.elRef.nativeElement.getElementsByTagName('input')[0] : this.elRef.nativeElement;
      if (!this.nativeEl) {
        console.warn('AngularFormsCurrencyMaskDirective: A elRef of type input is required for the directive to be initiated.');
        return;
      }
      this.control.valueChanges.pipe(this.validateOnInit ? startWith(this.control.value) : tap(() => {}), filter(value => {
        const lastValueWasChanged = this.valueHasChanged;
        this.valueHasChanged = false;
        return !this.previousValue || unmaskedNumericValueFor(value) !== unmaskedNumericValueFor(this.previousValue) || hasNonDecimalCharacters(value) || !lastValueWasChanged;
      }), map(value => value?.toString() ?? ''), takeUntil(this.directiveExists$)).subscribe(value => {
        this.adjustCursorIfSeparator(value);
        if (this.previousValue) {
          this.setValue(this.maskedValueFor(value), value.length < this.previousValue?.length);
        }
      });
    }
    ngOnDestroy() {
      //this.directiveExists$.next();
      this.directiveExists$.unsubscribe();
    }
    setValue(nextValue, removing = false) {
      let nextCursorPosition = cursorPositionFor(this.nativeEl);
      if (nextValue && this.previousValue) {
        nextCursorPosition = nextCursorPosition <= this.prefix.length + 1 ? nextValue.length : nextCursorPositionFor(this.nativeEl, this.previousValue, nextValue, true, true, removing);
      }
      const wasInitialValue = this.valueHasChanged;
      this.valueHasChanged = !!this.previousValue;
      this.previousValue = nextValue;
      if (this.control) {
        this.control.setValue(nextValue, {
          emitEvent: false
        });
        this.control.setValue(unmaskedNumericValueFor(nextValue), {
          emitEvent: true,
          emitModelToViewChange: false
        });
      }
      if (wasInitialValue) {
        nextCursorPosition = nextValue.length + 1;
      }
      setCursorPositionFor(this.nativeEl, nextCursorPosition);
    }
    maskedValueFor(value) {
      return maskedNumericValueFor(value, this.thousandsSeparator, this.decimalSeparator, this.prefix, this.digitsAfterSeparator, this.maxIntegerDigits, this.allowNegatives);
    }
    adjustCursorIfSeparator(value) {
      const decimalSeparatorPressed = value.indexOf(this.decimalSeparator) !== value.lastIndexOf(this.decimalSeparator);
      if (decimalSeparatorPressed) {
        const curPos = cursorPositionFor(this.elRef);
        const nextPos = curPos - 1 <= value.indexOf(this.decimalSeparator) ? value.length : value.indexOf(this.decimalSeparator) + 1;
        setCursorPositionFor(this.elRef, nextPos);
      }
    }
    static {
      this.ɵfac = function AngularFormsCurrencyMaskDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AngularFormsCurrencyMaskDirective)(i0.ɵɵdirectiveInject(i1.NgControl, 10), i0.ɵɵdirectiveInject(i0.ElementRef));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: AngularFormsCurrencyMaskDirective,
        selectors: [["", "angularFormsCurrency", ""]],
        inputs: {
          ngControl: "ngControl",
          prefix: "prefix",
          thousandsSeparator: "thousandsSeparator",
          decimalSeparator: "decimalSeparator",
          digitsAfterSeparator: "digitsAfterSeparator",
          maxIntegerDigits: "maxIntegerDigits",
          allowNegatives: "allowNegatives",
          validateOnInit: "validateOnInit"
        }
      });
    }
  }
  return AngularFormsCurrencyMaskDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
function maskFormatValidator(masks) {
  return control => {
    if (!control.value) {
      return null;
    }
    if (Array.isArray(masks)) {
      if (!masks.find(mask => mask.length === control.value.length)) {
        return {
          invalidLength: true
        };
      }
    } else {
      if (control.value.length !== masks.length) {
        return {
          invalidLength: true
        };
      }
    }
    return null;
  };
}
let AngularFormsMaskDirective = /*#__PURE__*/(() => {
  class AngularFormsMaskDirective {
    /**
     * Mask formats, accepts a single or multiple, matching by order. e.g "DDD-WWW.CCC"
     *
     * D: numbers; C: letters; W: both; All other characters are treated as part of the mask just displayed. */
    set angularFormsMask(value) {
      this.mask = Array.isArray(value) ? [...value].sort((a, b) => a.length - a.length) : value;
    }
    constructor(selfNgControl, elRef) {
      this.selfNgControl = selfNgControl;
      this.elRef = elRef;
      /** Add validation so that the input should match the length of the mask, else returns `invalidLength` validation error at the `ngControl`. */
      this.validateMaskInput = false;
      /** Set clear values to the formControl */
      this.unmasked = false;
      this.previousValue = '';
      this.directiveExists$ = new Subject();
    }
    ngOnInit() {
      if (!this.mask) {
        console.warn('AngularFormsMaskDirective: A Mask value is required for the directive to be initiated.');
        return;
      }
      this.control = this.selfNgControl?.control ?? this.ngControl;
      if (!this.control) {
        console.warn('AngularFormsMaskDirective: A FormControl value is required for the directive to be initiated.');
        return;
      }
      this.nativeEl = this.elRef.nativeElement.hasChildNodes() ? this.elRef.nativeElement.getElementsByTagName('input')[0] : this.elRef.nativeElement;
      if (!this.nativeEl) {
        console.warn('AngularFormsMaskDirective: A elRef of type input is required for the directive to be initiated.');
        return;
      }
      if (this.validateMaskInput) {
        this.control.setValidators([Validators.required, maskFormatValidator(this.mask)]);
      }
      this.control.valueChanges.pipe(startWith(this.control.value), takeUntil(this.directiveExists$)).subscribe(value => {
        if (value) {
          this.setValue(this.maskValueFor(value));
        }
      });
    }
    ngOnDestroy() {
      this.directiveExists$.next(true);
      this.directiveExists$.unsubscribe();
    }
    setValue(nextValue) {
      const nextCursorPosition = nextValue ? nextCursorPositionFor(this.nativeEl, this.previousValue, nextValue) : cursorPositionFor(this.nativeEl);
      this.previousValue = nextValue;
      this.control?.setValue(nextValue, {
        emitEvent: false
      });
      if (this.unmasked && nextValue) {
        this.control?.setValue(unmaskedValueFor(nextValue), {
          emitEvent: false,
          emitModelToViewChange: false
        });
      }
      this.elRef.nativeElement.addEventListener('focusin', () => {
        setCursorPositionFor(this.nativeEl, nextCursorPosition);
      });
    }
    maskValueFor(value) {
      const unmaskedValue = unmaskedValueFor(value);
      const nextMask = !Array.isArray(this.mask) ? this.mask : this.mask.find(mask => unmaskedValueFor(mask).length >= unmaskedValue.length) || this.mask[this.mask.length - 1];
      return matchAndReplaceFor(unmaskedValue, nextMask);
    }
    static {
      this.ɵfac = function AngularFormsMaskDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AngularFormsMaskDirective)(i0.ɵɵdirectiveInject(i1.NgControl, 10), i0.ɵɵdirectiveInject(i0.ElementRef));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: AngularFormsMaskDirective,
        selectors: [["", "angularFormsMask", ""]],
        inputs: {
          ngControl: "ngControl",
          angularFormsMask: "angularFormsMask",
          validateMaskInput: "validateMaskInput",
          unmasked: "unmasked"
        }
      });
    }
  }
  return AngularFormsMaskDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { AngularFormsCurrencyMaskDirective, AngularFormsMaskDirective, cursorPositionFor, hasNonDecimalCharacters, maskFormatValidator, maskedNumericValueFor, matchAndReplaceFor, nextCursorPositionFor, setCursorPositionFor, unmaskedNumericValueFor, unmaskedValueFor };
